/**
 * Padding, as one control with a link toggle.
 *
 * Linked is the normal case — a card wants the same space all round — so the two axes move
 * together until someone unlinks them. Unlinking keeps whatever was already set rather than
 * resetting, so the control never loses work.
 */

import type { CustomFieldRender } from '@puckeditor/core';
import type { PaddingValue } from '@lp/shared';
import { readPadding } from '@lp/shared';
import { Link2, Unlink2 } from 'lucide-react';
import { SPACE_OPTIONS } from '@lp/blocks';

type Props = Parameters<CustomFieldRender<PaddingValue>>[0];

function Select({
  value,
  onChange,
  disabled,
  id,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  id?: string;
}): React.ReactElement {
  return (
    <select
      id={id}
      className="lpf__input lpf__select"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    >
      {SPACE_OPTIONS.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function PaddingField({ value, onChange, readOnly }: Props): React.ReactElement {
  const v = readPadding(value);

  const set = (patch: Partial<PaddingValue>): void => onChange({ ...v, ...patch });

  // While linked, one number drives both.
  const setBoth = (next: string): void => set({ y: next, x: next });

  return (
    <div className="lpf lpf--padding">
      <div className="lpf__pad-head">
        <button
          type="button"
          className={`lpf__linkbtn${v.linked ? ' lpf__linkbtn--on' : ''}`}
          disabled={readOnly}
          aria-pressed={v.linked}
          title={
            v.linked
              ? 'Linked — one value all round. Click to set the sides separately.'
              : 'Unlinked — sides set separately. Click to link them.'
          }
          onClick={() =>
            // Linking adopts the vertical value, which is the one people set first.
            set(v.linked ? { linked: false } : { linked: true, x: v.y })
          }
        >
          {v.linked ? <Link2 size={14} strokeWidth={2.2} /> : <Unlink2 size={14} strokeWidth={2.2} />}
          {v.linked ? 'Linked' : 'Separate'}
        </button>
      </div>

      {v.linked ? (
        <label className="lpf__row">
          <span className="lpf__label">All sides</span>
          <Select value={v.y} onChange={setBoth} disabled={readOnly} />
        </label>
      ) : (
        <div className="lpf__pad-pair">
          <label className="lpf__row">
            <span className="lpf__label">Top &amp; bottom</span>
            <Select value={v.y} onChange={(y) => set({ y })} disabled={readOnly} />
          </label>
          <label className="lpf__row">
            <span className="lpf__label">Sides</span>
            <Select value={v.x} onChange={(x) => set({ x })} disabled={readOnly} />
          </label>
        </div>
      )}
    </div>
  );
}
